import {
  ContractStatusType,
  SignatureStatusType,
} from "@/src/store/apis/contracts/interface";
import { ContractNoticeType } from "@/src/components/molecule/ContractStatusNotice";
import { ContractStatusInfo } from "@/src/hooks/useContractProgress";

interface GetContractNoticeTypeParams {
  contractStatus: ContractStatusType | null;
  signatureStatus: SignatureStatusType | null;
  poNo?: string | null;
  scNo?: string | null;
  isOnlyReceivedPo?: boolean;
  isExporterPoSigned?: boolean;
  isImporterPoSigned?: boolean;
  isExporterScSigned?: boolean;
  isImporterScSigned?: boolean;
  isPending?: boolean;
  isSentSc?: boolean;
  hasLinkedBefore?: boolean;
  userRole?: "EXPORTER" | "IMPORTER";
  isLeadRole?: boolean; // true if user initiated the contract
  isRejected?: boolean; // 반려 상태
  isLinkedBuyer?: boolean; // 거래처 연결 여부 (buyer.linkedExporter 존재 여부)
  rejectedByMe?: boolean; // true if current user rejected the document
  contractStatusInfo?: ContractStatusInfo | null; // 서버에서 제공하는 정확한 상태 정보
}

export function getContractNoticeType({
  contractStatus,
  signatureStatus,
  poNo,
  scNo,
  isOnlyReceivedPo,
  isExporterPoSigned,
  isImporterPoSigned,
  isExporterScSigned = false,
  isImporterScSigned = false,
  isPending,
  isSentSc,
  hasLinkedBefore,
  userRole = "EXPORTER",
  isLeadRole = true,
  isRejected = false,
  isLinkedBuyer = false,
  rejectedByMe = false,
  contractStatusInfo,
}: GetContractNoticeTypeParams): ContractNoticeType | null {
  // Check if scNo and poNo have actual values (not just "-" or empty)
  const hasScNo = scNo && scNo !== "-";
  const hasPoNo = poNo && poNo !== "-";

  // 실제 서명 상태로 계약서 존재 여부 판단
  const hasSCContract = isExporterScSigned || isImporterScSigned;

  // ===== STEP 1: Lead/Follow 판단 (가장 중요) =====
  // 외부에서 히스토리 기반으로 판단한 값을 사용
  const actualLeadRole = isLeadRole;

  // contractStatusInfo를 우선적으로 사용하여 정확한 상태 판단
  if (contractStatusInfo?.currentStatus) {
    const { currentStatus } = contractStatusInfo;

    switch (currentStatus) {
      case "SC_SIGNATURE_REQUIRED":
        if (userRole === "EXPORTER") {
          return actualLeadRole
            ? "EX_LEAD_SC_SIGNATURE_PENDING"
            : "EX_FOLLOW_SC_SIGNATURE_PENDING";
        }
        // IMPORTER
        return actualLeadRole
          ? "IM_LEAD_SC_SIGNATURE_REQUIRED"
          : "IM_FOLLOW_SC_SIGNATURE_REQUIRED";

      case "PO_SIGNATURE_REQUIRED":
        if (userRole === "EXPORTER") {
          return actualLeadRole
            ? "EX_LEAD_PO_SIGNATURE_REQUIRED"
            : "EX_FOLLOW_PO_SIGNATURE_REQUIRED";
        }
        // IMPORTER
        return actualLeadRole
          ? "IM_LEAD_PO_SIGNATURE_PENDING"
          : "IM_FOLLOW_PO_SIGNATURE_PENDING";
    }
  }

  // 계약 완료 - SC와 PO가 모두 있고 양쪽 서명이 완료되었는지 확인
  if (contractStatus === "COMPLETE") {
    // 실제로 SC와 PO가 모두 있고 서명이 완료되었는지 재확인
    if (
      hasScNo &&
      hasPoNo &&
      isExporterPoSigned &&
      isImporterPoSigned &&
      isExporterScSigned &&
      isImporterScSigned
    ) {
      if (userRole === "EXPORTER") {
        return actualLeadRole
          ? "EX_LEAD_CONTRACT_COMPLETE"
          : "EX_FOLLOW_CONTRACT_COMPLETE";
      } else {
        return actualLeadRole
          ? "IM_LEAD_CONTRACT_COMPLETE"
          : "IM_FOLLOW_CONTRACT_COMPLETE";
      }
    }
    // contractStatus는 COMPLETE이지만 실제로는 미완료 상태
    // 이 경우 아래 로직에서 적절한 상태를 찾도록 함
  }

  // 수입자 Lead에서 PO 서명 대기 중 상태 처리
  if (
    userRole === "IMPORTER" &&
    actualLeadRole &&
    hasPoNo &&
    !hasScNo &&
    contractStatus === "PROCESSING" &&
    isLinkedBuyer && // 거래처가 연동되어 있어야 서명 진행 가능
    (signatureStatus === "SENT" || signatureStatus === "REQUESTED")
  ) {
    return "IM_LEAD_PO_SIGNATURE_PENDING";
  }

  // 수출자 Lead에서 SC 서명 대기 중 상태 처리
  if (
    userRole === "EXPORTER" &&
    actualLeadRole &&
    hasScNo &&
    !hasPoNo &&
    contractStatus === "PROCESSING" &&
    isLinkedBuyer &&
    (signatureStatus === "SENT" || signatureStatus === "REQUESTED")
  ) {
    return "EX_LEAD_SC_SIGNATURE_PENDING";
  }

  // 새로운 상태 값들 처리
  if (
    contractStatus === "SC_DRAFT_NOT_LINKED" ||
    contractStatus === "PO_DRAFT_NOT_LINKED"
  ) {
    if (contractStatus === "SC_DRAFT_NOT_LINKED" && userRole === "EXPORTER") {
      return "EX_LEAD_SC_DRAFT";
    }
    if (contractStatus === "PO_DRAFT_NOT_LINKED" && userRole === "IMPORTER") {
      return "IM_LEAD_PO_DRAFT";
    }
  }

  // DRAFT 상태 처리 - 1단계
  if (signatureStatus === "DRAFT") {
    // 수출자 Lead: SC 생성
    if (userRole === "EXPORTER" && hasScNo && !hasPoNo) {
      if (!isLinkedBuyer) {
        // 거래처가 연동되지 않은 경우 초안 상태로 표시
        return "EX_LEAD_SC_DRAFT";
      }
      return "EX_LEAD_SC_SIGNATURE_REQUIRED";
    }

    // 수입자 Lead: PO 생성
    if (userRole === "IMPORTER" && hasPoNo && !hasScNo) {
      if (!isLinkedBuyer) {
        return "IM_LEAD_PO_DRAFT";
      }
      return "IM_LEAD_PO_SIGNATURE_REQUIRED";
    }

    // 수입자 Follow: SC를 받고 PO 생성 필요
    if (userRole === "IMPORTER" && hasScNo && !hasPoNo) {
      return "IM_FOLLOW_PO_CREATE_REQUIRED";
    }

    // 수출자 Follow: PO를 받고 SC 생성 필요
    if (userRole === "EXPORTER" && hasPoNo && !hasScNo) {
      return "EX_FOLLOW_SC_CREATE_REQUIRED";
    }
  }

  // NOT_LINKED 상태에서 DRAFT가 아닌 경우
  if (contractStatus === "NOT_LINKED") {
    // 수입자가 SC를 받고 PO를 생성해야 하는 경우
    if (userRole === "IMPORTER" && hasScNo && !hasPoNo) {
      return "IM_FOLLOW_PO_CREATE_REQUIRED";
    }

    return null;
  }

  // 계약이 완전히 완료된 경우 (PO와 SC 모두 양쪽 서명 완료)
  if (
    hasPoNo &&
    hasScNo &&
    isExporterPoSigned &&
    isImporterPoSigned &&
    isExporterScSigned &&
    isImporterScSigned
  ) {
    if (userRole === "EXPORTER") {
      return actualLeadRole
        ? "EX_LEAD_CONTRACT_COMPLETE"
        : "EX_FOLLOW_CONTRACT_COMPLETE";
    } else {
      return actualLeadRole
        ? "IM_LEAD_CONTRACT_COMPLETE"
        : "IM_FOLLOW_CONTRACT_COMPLETE";
    }
  }

  // 반려 상태 체크를 가장 먼저 수행 (SC 또는 PO 반려)
  if (isRejected) {
    // 1. 양쪽이 모두 서명한 문서는 반려 대상에서 제외
    const isScFullySigned = isExporterScSigned && isImporterScSigned;
    const isPoFullySigned = isExporterPoSigned && isImporterPoSigned;

    // 2. 현재 진행 중인 단계의 문서가 반려된 것으로 판단
    // PO가 존재하고 SC는 완전히 서명되었다면 → PO 반려
    if (hasPoNo && isScFullySigned && !isPoFullySigned) {
      if (userRole === "EXPORTER") {
        // 수출자는 PO를 반려할 수 있음
        // 수출자가 PO를 반려하면 항상 본인이 반려한 것
        return actualLeadRole ? "EX_LEAD_PO_REJECTED" : "EX_FOLLOW_PO_REJECTED";
      } else {
        // 수입자는 PO를 반려할 수 없음 (수입자가 PO를 만들기 때문)
        // 따라서 항상 수출자가 반려한 것으로 간주
        return actualLeadRole ? "IM_LEAD_PO_REJECTED" : "IM_FOLLOW_PO_REJECTED";
      }
    }
    // SC가 존재하고 완전히 서명되지 않았다면 → SC 반려
    else if (hasScNo && !isScFullySigned) {
      if (userRole === "EXPORTER") {
        // 수출자는 SC를 반려할 수 없음 (수출자가 SC를 만들기 때문)
        // 따라서 항상 수입자가 반려한 것으로 간주
        return actualLeadRole ? "EX_LEAD_SC_REJECTED" : "EX_FOLLOW_SC_REJECTED";
      } else {
        // 수입자는 SC를 반려할 수 있음
        // 수입자가 SC를 반려하면 항상 본인이 반려한 것
        return actualLeadRole ? "IM_LEAD_SC_REJECTED" : "IM_FOLLOW_SC_REJECTED";
      }
    }
    // PO만 존재하는 경우 → PO 반려
    else if (hasPoNo && !hasScNo) {
      if (userRole === "EXPORTER") {
        // 수출자는 PO를 반려할 수 있음
        return actualLeadRole ? "EX_LEAD_PO_REJECTED" : "EX_FOLLOW_PO_REJECTED";
      } else {
        // 수입자는 PO를 반려할 수 없음
        return actualLeadRole ? "IM_LEAD_PO_REJECTED" : "IM_FOLLOW_PO_REJECTED";
      }
    }
  }

  // PO가 양쪽 모두 서명한 경우 (하지만 SC는 아직 완료되지 않음)
  if (isExporterPoSigned && isImporterPoSigned && !isImporterScSigned) {
    if (userRole === "EXPORTER") {
      if (actualLeadRole) {
        // Lead인데 SC가 없다면 문제가 있는 상태
        return null;
      } else {
        // Follow: SC 생성 필요 또는 서명 대기
        if (!hasSCContract) {
          return "EX_FOLLOW_SC_CREATE_REQUIRED";
        } else if (isExporterScSigned && !isImporterScSigned) {
          // 수출자는 SC 서명했지만 수입자는 아직
          return "EX_FOLLOW_SC_SIGNATURE_PENDING";
        }
      }
    } else {
      if (actualLeadRole) {
        // Lead: SC 대기 중
        if (!hasSCContract) {
          return "IM_LEAD_PO_SIGNED";
        }
      } else {
        // Follow: SC 서명 필요
        if (hasSCContract && isExporterScSigned && !isImporterScSigned) {
          return "IM_FOLLOW_SC_SIGNATURE_REQUIRED";
        }
      }
    }
  }

  // PO가 있고 수출자가 서명하지 않은 경우
  if (hasPoNo && !isExporterPoSigned && isImporterPoSigned) {
    if (userRole === "EXPORTER") {
      // SC가 없으면 Follow 케이스 (수입자가 먼저 PO를 보냄)
      if (!hasScNo) {
        return "EX_FOLLOW_PO_SIGNATURE_REQUIRED";
      }
      return "EX_LEAD_PO_SIGNATURE_REQUIRED";
    } else {
      // 수입자 Follow에서 PO 생성 후 서명 요청 대기 중
      if (
        !actualLeadRole &&
        contractStatus === "PROCESSING" &&
        (signatureStatus === "SENT" || signatureStatus === "REQUESTED")
      ) {
        return "IM_FOLLOW_PO_SIGNATURE_PENDING";
      }
      // 수입자 역할에서도 Lead/Follow 구분
      return actualLeadRole
        ? "IM_LEAD_PO_SIGNATURE_REQUIRED"
        : "IM_FOLLOW_PO_SIGNATURE_REQUIRED";
    }
  }

  // SC가 서명되었고 PO를 기다리는 중
  if (
    hasScNo &&
    signatureStatus &&
    signatureStatus === "APPROVED" &&
    !hasPoNo
  ) {
    if (userRole === "EXPORTER") {
      return "EX_LEAD_SC_SIGNED";
    } else {
      // 수입자 입장에서는 PO 생성이 필요함
      return "IM_FOLLOW_PO_CREATE_REQUIRED";
    }
  }

  // 수입자가 SC 서명이 필요한 경우 (PO 서명 완료 후 SC 서명 대기)
  if (
    userRole === "IMPORTER" &&
    hasPoNo &&
    isExporterPoSigned &&
    isImporterPoSigned &&
    !hasScNo &&
    signatureStatus &&
    ["SENT", "PENDING", "REQUESTED"].includes(signatureStatus)
  ) {
    return actualLeadRole
      ? "IM_LEAD_SC_SIGNATURE_REQUIRED"
      : "IM_FOLLOW_SC_SIGNATURE_REQUIRED";
  }

  // SC 서명이 진행 중인 경우 (수출자는 서명했지만 수입자는 아직)
  if (
    (signatureStatus === "REQUESTED" ||
      signatureStatus === "SENT" ||
      signatureStatus === "PENDING") &&
    isExporterScSigned &&
    !isImporterScSigned
  ) {
    if (userRole === "IMPORTER") {
      return actualLeadRole
        ? "IM_LEAD_SC_SIGNATURE_REQUIRED"
        : "IM_FOLLOW_SC_SIGNATURE_REQUIRED";
    } else {
      return actualLeadRole
        ? "EX_LEAD_SC_SIGNATURE_PENDING"
        : "EX_FOLLOW_SC_SIGNATURE_PENDING";
    }
  }

  // SC 서명 상태별 처리
  if (hasScNo) {
    switch (signatureStatus) {
      case "DRAFT":
        // 수입자이고 PO가 없는 경우 - PO 생성 필요
        if (userRole === "IMPORTER" && !hasPoNo) {
          return "IM_FOLLOW_PO_CREATE_REQUIRED";
        }
        // 거래처 연동이 안 된 경우
        if (!hasLinkedBefore) {
          return userRole === "EXPORTER" ? "EX_LEAD_SC_DRAFT" : null;
        }
        // 거래처 연동이 되었지만 서명 요청을 안 한 경우
        if (!isSentSc) {
          if (userRole === "EXPORTER") {
            return actualLeadRole
              ? "EX_LEAD_SC_SIGNATURE_REQUIRED"
              : "EX_FOLLOW_SC_SIGNATURE_REQUIRED";
          } else {
            return "IM_LEAD_SC_SIGNATURE_REQUIRED";
          }
        }
        break;

      case "SENT":
      case "PENDING":
      case "REQUESTED":
        // SC 서명 대기중
        if (userRole === "EXPORTER") {
          // Follow 케이스인지 확인 (PO가 있고 수출자가 Lead가 아닌 경우)
          if (hasPoNo && !actualLeadRole) {
            return "EX_FOLLOW_SC_SIGNATURE_PENDING";
          }
          return "EX_LEAD_SC_SIGNATURE_PENDING";
        } else {
          // 수입자 Follow 케이스 (수출자 주도)
          if (!actualLeadRole && contractStatus === "PROCESSING") {
            // 이미 서명 요청이 된 상태이므로 서명 필요만 표시
            return "IM_FOLLOW_SC_SIGNATURE_REQUIRED";
          }
          // 수입자는 SC 서명이 필요함
          return actualLeadRole
            ? "IM_LEAD_SC_SIGNATURE_REQUIRED"
            : "IM_FOLLOW_SC_SIGNATURE_REQUIRED";
        }

      case "APPROVED":
        // SC가 승인되었고 PO를 기다리는 경우
        if (!hasPoNo) {
          if (userRole === "EXPORTER") {
            return "EX_LEAD_SC_SIGNED";
          } else {
            // 수입자는 PO 생성이 필요함
            return "IM_FOLLOW_PO_CREATE_REQUIRED";
          }
        }
        break;

      default:
        break;
    }
  }

  return null;
}
